import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Calendar, Plus, Clock, Tag, WhatsappLogo } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { buildAppointmentWhatsappLink } from "@/lib/whatsappLinks";

const TYPE_LABELS = {
    consultation: "Consultation",
    first_fitting: "First fitting",
    review: "Review",
    refinement: "Refinement",
    delivery: "Delivery",
    general: "General",
};

const TYPE_TONES = {
    consultation: "bg-sky-50 text-sky-700",
    first_fitting: "bg-violet-50 text-violet-700",
    review: "bg-emerald-50 text-emerald-700",
    refinement: "bg-amber-50 text-amber-700",
    delivery: "bg-orange-50 text-orange-700",
    general: "bg-secondary text-foreground/80",
};

const TYPES = Object.keys(TYPE_LABELS);

function fmtDate(d) {
    try {
        return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return d; }
}

/* Mini component shown inside the expanded doctor case row.
   Lists appointments linked to this case + an inline scheduler. */
export default function CaseAppointments({ caseId, doctorId, patientId, patientName }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [adding, setAdding] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ date: "", time: "10:00", type: "review", notes: "" });

    const load = () => {
        api.get(`/cases/${caseId}/appointments`)
            .then((r) => setItems(r.data))
            .catch(() => setItems([]));
    };
    useEffect(() => { if (caseId) load(); }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

    const schedule = async () => {
        if (!form.date || !form.time) return toast.error("Pick a date and time");
        setBusy(true);
        try {
            await api.post("/appointments", {
                doctor_id: doctorId,
                date: form.date, time: form.time, type: form.type, notes: form.notes,
                case_id: caseId,
            });
            toast.success(`${TYPE_LABELS[form.type]} appointment scheduled`);
            setAdding(false);
            setForm({ date: "", time: "10:00", type: "review", notes: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to schedule");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white rounded-xl border border-border p-4" data-testid={`case-appointments-${caseId}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="overline text-muted-foreground flex items-center gap-1.5">
                    <Calendar size={14} weight="duotone" className="text-primary" /> Linked appointments
                </div>
                <Button size="sm" variant="outline" onClick={() => setAdding(!adding)} data-testid={`case-appt-add-${caseId}`}>
                    <Plus size={12} className="mr-1" /> Schedule
                </Button>
            </div>

            {adding && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 space-y-2 mb-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div>
                            <Label className="text-[11px] font-semibold">Type</Label>
                            <select
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                                className="w-full h-9 mt-1 text-xs rounded-lg border border-border bg-background px-2"
                                data-testid={`case-appt-type-${caseId}`}
                            >
                                {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="text-[11px] font-semibold">Date</Label>
                            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9 mt-1 text-xs" data-testid={`case-appt-date-${caseId}`} />
                        </div>
                        <div>
                            <Label className="text-[11px] font-semibold">Time</Label>
                            <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="h-9 mt-1 text-xs" data-testid={`case-appt-time-${caseId}`} />
                        </div>
                        <div className="flex items-end">
                            <Button size="sm" onClick={schedule} disabled={busy} className="w-full h-9" data-testid={`case-appt-save-${caseId}`}>
                                {busy ? "…" : "Confirm"}
                            </Button>
                        </div>
                    </div>
                    <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes for this visit" className="h-9 text-xs" data-testid={`case-appt-notes-${caseId}`} />
                </div>
            )}

            {items.length === 0 ? (
                <div className="text-xs text-muted-foreground italic">
                    No appointments linked to this case yet.{patientName ? ` Schedule a fitting or review for ${patientName}.` : ""}
                </div>
            ) : (
                <ul className="space-y-1.5">
                    {items.map((a) => {
                        const waUrl = buildAppointmentWhatsappLink(
                            { ...a, patient_name: a.patient_name || patientName },
                            {
                                name: user?.doctor_clinic_name || user?.clinic_name,
                                address: a.practice_location_address || user?.doctor_clinic_address || user?.address,
                            },
                        );
                        return (
                            <li key={a.id} className="text-xs flex items-center gap-2 flex-wrap" data-testid={`case-appt-${a.id}`}>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${TYPE_TONES[a.type] || TYPE_TONES.general}`}>
                                    <Tag size={10} weight="fill" className="inline mr-0.5" />
                                    {TYPE_LABELS[a.type] || a.type}
                                </span>
                                <span className="font-medium">{fmtDate(a.date)}</span>
                                <span className="text-muted-foreground inline-flex items-center gap-0.5"><Clock size={10} /> {a.time}</span>
                                {a.status && a.status !== "confirmed" && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-foreground/70 uppercase">{a.status}</span>
                                )}
                                {a.notes && <span className="text-muted-foreground italic truncate">, {a.notes}</span>}
                                {waUrl && a.status !== "cancelled" && a.status !== "completed" && (
                                    <button
                                        type="button"
                                        onClick={() => window.open(waUrl, "_blank", "noopener,noreferrer")}
                                        className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 hover:text-emerald-800 hover:underline"
                                        data-testid={`case-appt-whatsapp-${a.id}`}
                                        title="WhatsApp reminder"
                                    >
                                        <WhatsappLogo size={11} weight="duotone" /> WhatsApp
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
